
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { StoryCircle } from "./StoryCircle";
import { StoryUploadDialog } from "./StoryUploadDialog";
import { StoryViewer } from "./StoryViewer";
import { useAuth } from "@/contexts/AuthContext";
import { Plus } from "lucide-react";
import { Button } from "../ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

export const StoriesRow = () => {
  const { user } = useAuth();
  const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const isMobile = useIsMobile();

  const {
    data: stories,
    isLoading
  } = useQuery({
    queryKey: ["stories"],
    queryFn: async () => {
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const {
        data,
        error
      } = await supabase.from("stories").select(`
          *,
          profiles (
            username,
            avatar_url,
            full_name
          )
        `).gt("created_at", twentyFourHoursAgo).order("created_at", {
        ascending: true // Get oldest first
      });
      if (error) throw error;
      return data || [];
    }
  });

  // Get unique stories for display (one per user)
  const uniqueUserStories = stories?.reduce((acc, current) => {
    const existingStory = acc.find(story => story.user_id === current.user_id);
    if (!existingStory) {
      acc.push(current);
    }
    return acc;
  }, []);

  const selectedStory = stories?.find(story => story.id === selectedStoryId);

  if (isLoading) {
    return (
      <div className="flex gap-4 p-4 overflow-x-auto scrollbar-none">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="w-16 h-16 rounded-full bg-black/20 flex-shrink-0 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!stories?.length && !user) {
    return null;
  }

  return (
    <>
      <div className="relative bg-black/20 p-4 rounded-lg mb-4">
        <div className="flex gap-4 overflow-x-auto scrollbar-none items-center">
          {user && (
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="w-16 h-16 flex items-center justify-center flex-shrink-0 transition-colors bg-[#0EA5E9] text-white mx-0 -mt-2 px-0 py-0 rounded-2xl hover:bg-[#0EA5E9]/90"
            >
              <Plus className="w-6 h-6" />
            </Button>
          )}
          
          <div className="flex gap-4 flex-nowrap">
            {uniqueUserStories?.map(story => (
              <StoryCircle 
                key={story.id} 
                story={story} 
                onSelect={() => {
                  // Find the first story from this user
                  const firstUserStory = stories.find(s => s.user_id === story.user_id);
                  setSelectedStoryId(firstUserStory?.id);
                }} 
                isMobile={isMobile}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedStory && (
        <StoryViewer
          open={!!selectedStoryId}
          onOpenChange={open => !open && setSelectedStoryId(null)}
          storyUrl={selectedStory.media_url}
          mediaType={selectedStory.media_type}
          storyId={selectedStory.id}
          isOwnStory={selectedStory.user_id === user?.id}
          onComplete={() => {
            const currentIndex = stories.findIndex(s => s.id === selectedStoryId);
            if (currentIndex < stories.length - 1) {
              setSelectedStoryId(stories[currentIndex + 1].id);
            } else {
              setSelectedStoryId(null);
            }
          }}
          onPrevious={() => {
            const currentIndex = stories.findIndex(s => s.id === selectedStoryId);
            if (currentIndex > 0) {
              setSelectedStoryId(stories[currentIndex - 1].id);
            }
          }}
          totalStories={stories.length}
          currentStoryIndex={stories.findIndex(s => s.id === selectedStoryId)}
          textOverlay={selectedStory.text_overlay}
          audioUrl={selectedStory.audio_url}
          audioStartTime={selectedStory.audio_start_time}
          username={selectedStory.profiles?.username}
          audioTitle={selectedStory.audio_title}
          audioArtist={selectedStory.audio_artist}
        />
      )}

      <StoryUploadDialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen} />
    </>
  );
};
